import { Sentiment } from '@conversed/shared/src/central/constants/sentiment';
import type { TFunction } from 'i18next';

// Single source of truth for sentiment colours/labels/icons across the Home v2
// Intelligence UI (the sentiment bar, the split bar, the percentage label, and the
// User Sentiment chip). Change a level's colours here and every consumer updates.
//
// Tailwind classes must be full literal strings (the JIT scanner can't see
// `bg-${hue}-50`), so each level lists its own classes - but they all live in one
// place per level, so a colour change is a single edit.

// * One rule for the whole Intelligence section: a topic's sentiment is its
// * biggest bucket, and its percentage is that bucket's share of ALL the topic's
// * questions. The table's bar, colour and chip, the topic cards and What's
// * changed all read it from the shared helper, so they cannot disagree - and the
// * Weekly Intelligence Brief email reads the same one.
export {
  type DominantSentimentShare,
  resolveDominantShare,
} from '@conversed/shared/src/utils/intelligence-change.utils';

type SentimentDisplay = {
  labelKey: string;
  icon: string;
  // Bar fill (bg-) and the matching percentage label (text-).
  barColorClass: string;
  // One shade darker, for hovering a clickable bar segment (the View modal filter).
  barHoverColorClass: string;
  percentColorClass: string;
  // Pill chip: background + text + border together.
  chipClass: string;
};

export const SENTIMENT_DISPLAY: Record<Sentiment, SentimentDisplay> = {
  [Sentiment.Unhappy]: {
    labelKey: 'sentiment.unhappy',
    icon: 'sentiment_dissatisfied',
    barColorClass: 'bg-red-500',
    barHoverColorClass: 'hover:bg-red-600',
    percentColorClass: 'text-red-500',
    chipClass: 'bg-red-50 text-red-500 border border-red-200',
  },
  [Sentiment.Neutral]: {
    labelKey: 'sentiment.neutral',
    icon: 'sentiment_satisfied',
    // Neutral reads as grey everywhere (gray-300 bar segment), matching the
    // Weekly Intelligence Brief. Text sits a shade darker so it stays readable.
    barColorClass: 'bg-gray-300',
    barHoverColorClass: 'hover:bg-gray-400',
    percentColorClass: 'text-gray-500',
    chipClass: 'bg-gray-50 text-gray-500 border border-gray-200',
  },
  [Sentiment.Happy]: {
    labelKey: 'sentiment.happy',
    icon: 'emoji_emotions',
    barColorClass: 'bg-emerald-600',
    barHoverColorClass: 'hover:bg-emerald-700',
    percentColorClass: 'text-emerald-600',
    chipClass: 'bg-emerald-50 text-emerald-700 border border-emerald-300',
  },
};

// * The sentiment word inside a sentence ("Sentiment moved from neutral to happy",
// * "No unhappy questions"). Reuses the capitalised `sentiment.*` labels and
// * lowercases them, which is correct for both supported languages (en, nl) -
// * a language that capitalises nouns would need its own key instead.
export const translateSentimentInSentence = (
  translate: TFunction,
  sentiment: Sentiment,
): string =>
  translate(SENTIMENT_DISPLAY[sentiment].labelKey).toLocaleLowerCase();
